import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import Swal from 'sweetalert2'
import {
  useDeleteChannelProductsMutation,
  useGetStockSkuListQuery,
  useLinkChannelProductMutation,
} from '../../../hooks/RTK_Service/MapUnmapProductData'
import { useGetAllWarehousesQuery } from '../../../hooks/RTK_Service/warehouse'
import { useSaveStockDetailMutation } from '../../../hooks/RTK_Service/stockApi'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
  bgcolor: 'background.paper',
  borderRadius: '7px',
  boxShadow: 24,
  p: 4,
}

export default function UnmappedTable({ UnmapProductData, pageNumber, handlePageNumber, refreshData }) {
  const [openLinkModal, setOpenLinkModal] = useState(false)
  const [mapChannelInfo, setMapChannelInfo] = useState(null)
  const [selectedSku, setSelectedSku] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [newSku, setNewSku] = useState('')
  const [newQuantity, setNewQuantity] = useState('')
  const [newWarehouseId, setNewWarehouseId] = useState('')

  const { data: skuData, isLoading: skuLoading } = useGetStockSkuListQuery()
  const skuOptions = skuData?.skus ?? []

  const { data: warehouseData } = useGetAllWarehousesQuery({ page: 1, limit: 1000 })
  const warehouses = warehouseData?.data?.content ?? []

  const [linkChannelProduct, { isLoading: linking }] = useLinkChannelProductMutation()
  const [deleteChannelProducts, { isLoading: deleting }] = useDeleteChannelProductsMutation()
  const [saveStockDetail, { isLoading: savingStock }] = useSaveStockDetailMutation()

  const handleOpenMapSkuModal = (channel_info) => {
    setMapChannelInfo(channel_info)
    setSelectedSku(null)
    setOpenLinkModal(true)
  }

  const handleCloseMapSkuModal = () => {
    setMapChannelInfo(null)
    setOpenLinkModal(false)
    setSelectedSku(null)
    setNewSku('')
    setNewQuantity('')
    setNewWarehouseId('')
  }

  const handleCreateAndLink = async () => {
    if (!newSku.trim()) {
      Swal.fire('Warning', 'Please enter a SKU.', 'warning')
      return
    }
    if (newQuantity === '' || parseInt(newQuantity) < 0) {
      Swal.fire('Warning', 'Please enter a valid quantity.', 'warning')
      return
    }
    try {
      await saveStockDetail({
        sku: newSku.trim(),
        warehouseId: newWarehouseId ? parseInt(newWarehouseId) : null,
        quantity: parseInt(newQuantity),
      }).unwrap()
      const res = await linkChannelProduct({
        channelProductId: mapChannelInfo.id,
        sku: newSku.trim(),
      }).unwrap()
      Swal.fire('Success', res?.message || 'Created and linked successfully.', 'success')
      handleCloseMapSkuModal()
      if (refreshData) refreshData()
    } catch (err) {
      Swal.fire('Error', err?.data?.message || 'Operation failed.', 'error')
    }
  }

  const handleLink = async () => {
    if (!selectedSku) {
      Swal.fire('Warning', 'Please select a SKU to link.', 'warning')
      return
    }
    try {
      const res = await linkChannelProduct({
        channelProductId: mapChannelInfo.id,
        sku: selectedSku,
      }).unwrap()
      Swal.fire('Success', res?.message || 'Linked successfully.', 'success')
      handleCloseMapSkuModal()
      if (refreshData) refreshData()
    } catch (err) {
      Swal.fire('Error', err?.data?.message || 'Failed to link product.', 'error')
    }
  }

  const handleSingleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete?',
      text: 'Are you sure you want to delete this channel product?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete',
    })
    if (!result.isConfirmed) return
    try {
      const res = await deleteChannelProducts([id]).unwrap()
      Swal.fire('Deleted', res?.message || 'Deleted successfully.', 'success')
      setSelectedIds((prev) => prev.filter((x) => x !== id))
      if (refreshData) refreshData()
    } catch (err) {
      Swal.fire('Error', err?.data?.message || 'Failed to delete.', 'error')
    }
  }

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) {
      Swal.fire('Warning', 'No products selected.', 'warning')
      return
    }
    const result = await Swal.fire({
      title: `Delete ${selectedIds.length} product(s)?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete all',
    })
    if (!result.isConfirmed) return
    try {
      const res = await deleteChannelProducts(selectedIds).unwrap()
      Swal.fire('Deleted', res?.message || 'Deleted successfully.', 'success')
      setSelectedIds([])
      if (refreshData) refreshData()
    } catch (err) {
      Swal.fire('Error', err?.data?.message || 'Failed to delete.', 'error')
    }
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(UnmapProductData?.content?.map((d) => d.id) ?? [])
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const allSelected =
    UnmapProductData?.content?.length > 0 &&
    UnmapProductData.content.every((d) => selectedIds.includes(d.id))

  return (
    <div>
      {selectedIds.length > 0 && (
        <div className="mb-2 d-flex align-items-center gap-2">
          <span className="text-muted">{selectedIds.length} selected</span>
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={deleting}
            onClick={handleBatchDelete}
          >
            {deleting ? <CircularProgress size={16} color="inherit" /> : 'Delete Selected'}
          </Button>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-hover mt-2">
          <thead style={{ borderBottom: '1px solid black' }}>
            <tr>
              <th scope="col">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selectedIds.length > 0 && !allSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th scope="col">CHANNEL ID</th>
              <th scope="col">CHANNEL SKU</th>
              <th scope="col">CHANNEL PRODUCT ID</th>
              <th scope="col">CHANNEL PRODUCT TITLE</th>
              <th scope="col">CHANNEL PRICE</th>
              <th scope="col">CHANNEL SALE PRICE</th>
              <th scope="col">LINK</th>
              <th scope="col">DELETE</th>
            </tr>
          </thead>
          <tbody>
            {UnmapProductData?.content?.map((data) => (
              <tr key={'unmap' + data.id}>
                <td>
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(data.id)}
                    onChange={() => handleSelectOne(data.id)}
                  />
                </td>
                <td>{data?.channelId}</td>
                <td>{data.channelSku}</td>
                <td>{data.channelProductId}</td>
                <td>{data.channelProductTitle}</td>
                <td>{data.channelPrice}</td>
                <td>{data.channelSalePrice}</td>
                <td>
                  <button
                    onClick={() =>
                      handleOpenMapSkuModal({
                        id: data.id,
                        channel_sku: data.channelSku,
                        channel_product_title: data.channelProductTitle,
                      })
                    }
                    className="btn btn-sm btn-outline-warning"
                  >
                    MAP SKU
                  </button>
                </td>
                <td>
                  <button
                    onClick={() => handleSingleDelete(data.id)}
                    className="btn btn-sm btn-outline-danger"
                    disabled={deleting}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="right">
          <Stack spacing={2}>
            <Pagination
              color="primary"
              count={UnmapProductData?.totalPages}
              page={pageNumber}
              onChange={(e, v) => handlePageNumber(v)}
            />
          </Stack>
        </div>
      </div>

      <Modal
        open={openLinkModal}
        onClose={handleCloseMapSkuModal}
        aria-labelledby="link-modal-title"
      >
        <Box sx={style}>
          <Typography id="link-modal-title" variant="h6" component="h2">
            <i className="fa-solid fa-link fa-2x px-2" style={{ color: '#205dc5' }}></i>
            LINK PRODUCT
          </Typography>
          <Typography sx={{ mt: 2 }}>
            <b>CHANNEL SKU : </b> {mapChannelInfo?.channel_sku}
          </Typography>
          <Typography sx={{ my: 2 }}>
            <b>CHANNEL PRODUCT TITLE : </b> {mapChannelInfo?.channel_product_title}
          </Typography>

          <Autocomplete
            options={skuOptions}
            loading={skuLoading}
            value={selectedSku}
            onChange={(e, val) => setSelectedSku(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Master SKU *"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {skuLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            onClick={handleLink}
            disabled={linking || !selectedSku}
          >
            {linking ? <CircularProgress size={20} color="inherit" /> : 'Link'}
          </Button>

          <Divider sx={{ my: 2 }}>OR create new stock entry</Divider>

          <TextField
            fullWidth
            label="New SKU *"
            size="small"
            value={newSku}
            onChange={(e) => setNewSku(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Quantity *"
            type="number"
            size="small"
            inputProps={{ min: 0 }}
            value={newQuantity}
            onChange={(e) => setNewQuantity(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Warehouse *</InputLabel>
            <Select
              value={newWarehouseId}
              label="Warehouse *"
              onChange={(e) => setNewWarehouseId(e.target.value)}
            >
              <MenuItem value="">-- None --</MenuItem>
              {warehouses.map((w) => (
                <MenuItem key={w.warehouseId} value={w.warehouseId}>
                  {w.warehouseName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            fullWidth
            variant="outlined"
            color="success"
            disabled={savingStock || linking}
            onClick={handleCreateAndLink}
          >
            {savingStock || linking ? <CircularProgress size={20} color="inherit" /> : 'Create & Link'}
          </Button>
        </Box>
      </Modal>
    </div>
  )
}